// Ported from handover: window.tasklet.runTool → fetch('/api/sheet')
// Adapts the Sheets API v4 response shape (values[][]) to SheetRow objects

import React, { useState, useEffect } from 'react';
import { RefreshCw, AlertCircle, ExternalLink } from 'lucide-react';
import { SheetRow } from '../types';
import { isFutureDMY, parseDMY } from '../utils/helpers';
import { API_BASE } from '../config';

const SHEET_URL = 'https://docs.google.com/spreadsheets/d/1Zv9z2IdcWcLjq4_6mT1hAv_utwbSXDLbMmEAgIOKWi4/edit?gid=717669197';

type CellValue = string | number | boolean | null;

function serialToDate(serial: number): string {
  const d = new Date(Math.round((serial - 25569) * 86400 * 1000));
  return `${String(d.getUTCDate()).padStart(2,'0')}/${String(d.getUTCMonth()+1).padStart(2,'0')}/${d.getUTCFullYear()}`;
}

// Google Sheets stores a time-of-day as a fraction of a 24h day (e.g. 0.6666… = 16:00).
// The Sheets API returns the raw number, so format it as "h:mm AM/PM" (matches the
// sheet's "4:00 PM" convention) for the Arr/Dep/Flight time columns.
function fracToTime(frac: number): string {
  const totalMin = Math.round(frac * 1440) % 1440;
  const h = Math.floor(totalMin / 60);
  const m = totalMin % 60;
  const ampm = h < 12 ? 'AM' : 'PM';
  const h12 = h % 12 === 0 ? 12 : h % 12;
  return `${h12}:${String(m).padStart(2,'0')} ${ampm}`;
}

function toStr(v: CellValue | undefined): string | null {
  if (v === null || v === undefined || v === '') return null;
  if (typeof v === 'number') {
    // Excel/Sheets serial date
    if (v > 1000 && String(v).length > 4) return serialToDate(v);
    // Time-of-day fraction (only the time columns produce 0 < v < 1)
    if (v > 0 && v < 1) return fracToTime(v);
  }
  return String(v).trim() || null;
}

async function fetchSheetRows(): Promise<SheetRow[]> {
  const res = await fetch(`${API_BASE}/api/sheet`);
  if (!res.ok) throw new Error(`API error ${res.status}: ${await res.text()}`);

  const data = await res.json() as { values?: CellValue[][] };
  const rows = data.values ?? [];

  // Data starts at row index 3 (rows 0-2 are title/blank/header)
  return rows
    .slice(3)
    .filter(row => row[0])
    .map(row => ({
      name:            toStr(row[0]) ?? '',
      adults:          row[1] != null ? Number(row[1]) : null,
      infants:         row[2] != null ? Number(row[2]) : null,
      arrivalDate:     toStr(row[3]),
      departureDate:   toStr(row[4]),
      nights:          row[5] != null ? Number(row[5]) : null,
      arrivalTime:     toStr(row[6]),
      takeOffTime:     toStr(row[7]),
      welcomePack:     toStr(row[8]),
      specialRequests: toStr(row[9]),
      flightArrival:   toStr(row[10]),
      flightDeparture: toStr(row[11]),
    }))
    .sort((a, b) => parseDMY(a.arrivalDate).getTime() - parseDMY(b.arrivalDate).getTime());
}

export const SheetTab: React.FC = () => {
  const [rows, setRows]     = useState<SheetRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState<string | null>(null);
  const [showAll, setShowAll] = useState(false);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      setRows(await fetchSheetRows());
    } catch (e) {
      setError(String(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const futureRows   = rows.filter(r => isFutureDMY(r.arrivalDate));
  const displayRows  = showAll ? rows : futureRows;
  const missingFlights = futureRows.filter(r => !r.flightArrival && !r.flightDeparture).length;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <span className="loading loading-spinner loading-lg text-primary"></span>
        <span className="ml-3 text-base-content/60">Loading cleaning sheet...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="alert alert-error m-4">
        <AlertCircle size={16} />
        <span>Failed to load sheet: {error}</span>
      </div>
    );
  }

  return (
    <div className="p-4 space-y-4">
      {/* Stats + Controls */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex gap-2 flex-wrap">
          <div className="badge badge-primary badge-lg">{futureRows.length} upcoming</div>
          {missingFlights > 0 && (
            <div className="badge badge-warning badge-lg">{missingFlights} missing flights</div>
          )}
        </div>
        <div className="flex items-center gap-2">
          <label className="label cursor-pointer gap-2">
            <span className="label-text text-xs">Show all</span>
            <input
              type="checkbox"
              className="toggle toggle-xs"
              checked={showAll}
              onChange={e => setShowAll(e.target.checked)}
            />
          </label>
          <a href={SHEET_URL} target="_blank" rel="noreferrer" className="btn btn-xs btn-ghost gap-1">
            <ExternalLink size={12} /> Open Sheet
          </a>
          <button className="btn btn-xs btn-ghost gap-1" onClick={load}>
            <RefreshCw size={12} /> Refresh
          </button>
        </div>
      </div>

      {/* Table */}
      <div className="overflow-x-auto rounded-box border border-base-200">
        <table className="table table-xs table-zebra w-full">
          <thead>
            <tr>
              <th>Name</th>
              <th className="text-center">Ad</th>
              <th className="text-center">Ch</th>
              <th>Arrival</th>
              <th>Departure</th>
              <th className="text-center">Nights</th>
              <th>Arr Time</th>
              <th>Dep Time</th>
              <th>Flight In</th>
              <th>Flight Out</th>
              <th>Welcome Pack</th>
            </tr>
          </thead>
          <tbody>
            {displayRows.map((r, i) => {
              const isFuture     = isFutureDMY(r.arrivalDate);
              const missingFlight = isFuture && !r.flightArrival && !r.flightDeparture;
              return (
                <tr key={i} className={!isFuture ? 'opacity-40' : ''}>
                  <td className="font-medium whitespace-nowrap">{r.name}</td>
                  <td className="text-center">{r.adults ?? '—'}</td>
                  <td className="text-center">{r.infants ?? '—'}</td>
                  <td className="whitespace-nowrap">{r.arrivalDate ?? '—'}</td>
                  <td className="whitespace-nowrap">{r.departureDate ?? '—'}</td>
                  <td className="text-center">{r.nights ?? '—'}</td>
                  <td className="whitespace-nowrap">{r.arrivalTime ?? '—'}</td>
                  <td className="whitespace-nowrap">{r.takeOffTime ?? '—'}</td>
                  <td className={`whitespace-nowrap ${missingFlight ? 'text-warning' : ''}`}>
                    {r.flightArrival ?? (missingFlight ? '⚠ missing' : '—')}
                  </td>
                  <td className={`whitespace-nowrap ${missingFlight ? 'text-warning' : ''}`}>
                    {r.flightDeparture ?? (missingFlight ? '⚠ missing' : '—')}
                  </td>
                  <td>{r.welcomePack ?? '—'}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};
